import { createContext, useCallback, useContext, useState, ReactNode } from 'react'
import { Snackbar, IconButton, Box, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { LoyaltyPointsToast, LoyaltyToastPayload } from '@/components/LoyaltyPointsToast'

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  message: string
  type: ToastType
}

interface ToastContextProps {
  /** Plain message toast — top-center MUI Snackbar (unchanged). */
  alert: (message: string, type: ToastType) => void
  /**
   * Loyalty toast — top-right stacked card with member + points + tier.
   * Returns the toast id so callers can dismiss it programmatically.
   * Payload's `id` is optional; one is generated when missing.
   */
  notify: (payload: Omit<LoyaltyToastPayload, 'id'> & { id?: string }) => string
}

const BG_COLOR = {
  success: {
    text: '#FFFFFF',
    bg: '#33B15B',
  },
  error: {
    text: '#F5EFF7',
    bg: '#B90810',
  },
  info: {
    text: '#F5EFF7',
    bg: '#313030',
  },
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined)

// Monotonic counter — useId would be per-instance; we want a global stream so
// rapidly-fired notify() calls each get a unique id even from the same caller.
let nextToastSeq = 1
const makeToastId = () => `toast-${nextToastSeq++}`

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toast, setToast] = useState<Toast | null>(null)
  const [loyaltyToasts, setLoyaltyToasts] = useState<LoyaltyToastPayload[]>([])

  const alert = (message: string, type: ToastType) => {
    setToast({ message, type })
  }

  const notify = useCallback((payload: Omit<LoyaltyToastPayload, 'id'> & { id?: string }) => {
    const id = payload.id ?? makeToastId()
    setLoyaltyToasts((prev) => [...prev, { ...payload, id }])
    return id
  }, [])

  const dismissLoyaltyToast = useCallback((id: string) => {
    setLoyaltyToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const handleClose = () => {
    setToast(null)
  }

  return (
    <ToastContext.Provider value={{ alert, notify }}>
      {children}
      <Snackbar
        open={!!toast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleClose}
        autoHideDuration={4000}
      >
        {toast ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '344px',
              bgcolor: BG_COLOR[toast.type]?.bg ?? BG_COLOR.info.bg,
              color: BG_COLOR[toast.type]?.text ?? BG_COLOR.info.text,
              px: 2,
              py: '14px',
              borderRadius: 2,
              boxShadow: 3,
              minWidth: 300,
            }}
          >
            <Typography variant="body2">{toast.message}</Typography>
            <IconButton sx={{ padding: 0 }} onClick={handleClose}>
              <CloseIcon sx={{ color: 'white' }} />
            </IconButton>
          </Box>
        ) : undefined}
      </Snackbar>

      {/* Loyalty toasts — top-right stack. Sits OUTSIDE the MUI Snackbar so
          both can coexist without overlap. pointer-events: none on the
          wrapper so the empty area below the stack stays click-through. */}
      <Box
        sx={{
          position: 'fixed',
          top: 24,
          right: 24,
          zIndex: (theme) => theme.zIndex.snackbar + 1,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          maxWidth: 'calc(100vw - 48px)',
        }}
      >
        <Stack spacing={1.25} sx={{ pointerEvents: 'auto' }}>
          {loyaltyToasts.map((p) => (
            <LoyaltyPointsToast key={p.id} payload={p} onDismiss={dismissLoyaltyToast} />
          ))}
        </Stack>
      </Box>
    </ToastContext.Provider>
  )
}

export const useAlert = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
